import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';
import { useEffect } from 'react';

import {
  Accordion,
  Layout,
  OverlayTriggerTooltip,
  PillV2 as Pill,
  Select,
  SelectableCard,
} from '@utilities';
import { getPromptPillTooltip, getPromptPillValue } from '../../../../utilities/helpers';
import { GATES } from '@constants';

import { CUSTOMIZE_SAMPLE_FIELDS, OPTIONAL_GEN_REP_FIELDS } from '../../../../utilities/constants';

import styles from './_index.module.scss';

const GroupAdvancedPrompts = ({
  activePrompt,
  advancedPrompts = [],
  group,
  isCompleted,
  onClearAnswer,
  onUpdate,
  setActivePrompt,
}) => {
  const { client } = useStatsigClient();
  const { promptAnswers } = group;
  const showGenRepSample = client.checkGate(GATES.GEN_REP_SAMPLE);

  const GEN_REP_SAMPLE_TYPES = [{ label: 'Balance to Gen Rep (Census)', value: 'CENSUS' }];

  const hasAllowedGenRepFields = promptAnswers.every(
    (answer) => answer.values.length === 0 || OPTIONAL_GEN_REP_FIELDS.includes(answer.field)
  );

  const hasCustomizedSampleFields = promptAnswers.some(
    (answer) =>
      CUSTOMIZE_SAMPLE_FIELDS.includes(answer.field) &&
      answer.values.some((v) => v !== '' && v != null)
  );

  const handleSelectBalanced = () => {
    advancedPrompts.forEach((prompt) => onClearAnswer?.(prompt?.id));
    onUpdate?.({ genRepSampleType: GEN_REP_SAMPLE_TYPES[0].value });
  };

  const handleSelectCustom = () => {
    onUpdate?.({ genRepSampleType: null });
  };

  const handleSampleTypeSelect = (opt) => {
    onUpdate?.({ genRepSampleType: opt?.value });
  };

  const hasAnySelection = promptAnswers.some((a) => a.values.length > 0);

  const renderPromptPills = () => {
    return (
      <Layout.Flex.Column>
        <Layout.Flex as="ul" className={styles['group-advanced-prompts-card-list']}>
          {advancedPrompts.map((prompt) => {
            const currentPromptAnswers = promptAnswers.filter(
              ({ _delete, promptId }) => !_delete && promptId === prompt.id
            );
            const tooltip = getPromptPillTooltip({ prompt, promptAnswers });
            const value = getPromptPillValue({ prompt, promptAnswers });

            return (
              <Layout.Flex.Column as="li" gap="x-small" key={`advanced-prompt-${prompt.id}`}>
                <span className={styles['group-advanced-prompts-card-name']}>{prompt?.name}</span>
                <span>
                  <OverlayTriggerTooltip content={tooltip} isFullWidth>
                    <Pill
                      isActive={prompt === activePrompt}
                      isCompleted={isCompleted}
                      isModified={currentPromptAnswers.length > 0}
                      isRequired={prompt?.isRequired}
                      onClick={() => setActivePrompt(prompt)}
                      onClose={() => onClearAnswer(prompt?.id)}
                      value={value}
                    />
                  </OverlayTriggerTooltip>
                </span>
              </Layout.Flex.Column>
            );
          })}
        </Layout.Flex>
      </Layout.Flex.Column>
    );
  };

  useEffect(() => {
    if (
      hasAnySelection &&
      !isCompleted &&
      !hasAllowedGenRepFields &&
      group.genRepSampleType !== null
    ) {
      onUpdate?.({ genRepSampleType: null });
    }
  }, [hasAnySelection, hasAllowedGenRepFields, isCompleted, group.genRepSampleType, onUpdate]);

  return (
    <Accordion
      className={styles['group-advanced-prompts']}
      id="advanced-prompts-header"
      label="People"
    >
      <Layout.Flex gap="small">
        <Layout.Fill>
          {showGenRepSample ? (
            <SelectableCard
              header="Customize Your Sample"
              isActive={group.genRepSampleType === null}
              onClick={handleSelectCustom}
              text={renderPromptPills()}
            />
          ) : (
            renderPromptPills()
          )}
        </Layout.Fill>
        {showGenRepSample && (
          <Layout.Fill>
            <SelectableCard
              error={
                hasAnySelection && !hasCustomizedSampleFields && !hasAllowedGenRepFields
                  ? 'Balanced Sample is unavailable for narrowly defined samples.'
                  : undefined
              }
              header="Balanced Sample"
              isActive={group.genRepSampleType === GEN_REP_SAMPLE_TYPES[0].value}
              isDisabled={hasAnySelection && !hasAllowedGenRepFields}
              onClick={handleSelectBalanced}
              text={
                <Layout.Flex.Column>
                  <Select
                    isDisabled={hasAnySelection && !hasAllowedGenRepFields}
                    label="Sample Type"
                    onChange={handleSampleTypeSelect}
                    options={GEN_REP_SAMPLE_TYPES}
                    value={
                      GEN_REP_SAMPLE_TYPES.find((opt) => opt.value === group.genRepSampleType) ||
                      null
                    }
                  />
                </Layout.Flex.Column>
              }
            />
          </Layout.Fill>
        )}
      </Layout.Flex>
    </Accordion>
  );
};

GroupAdvancedPrompts.propTypes = {
  activePrompt: PropTypes.object,
  advancedPrompts: PropTypes.array,
  group: PropTypes.object.isRequired,
  isCompleted: PropTypes.bool,
  onClearAnswer: PropTypes.func,
  onUpdate: PropTypes.func,
  setActivePrompt: PropTypes.func,
};

export default GroupAdvancedPrompts;
